import { DEFAULT_GST, PRODUCT_SEARCH_INFO_MESSAGE, RS_SYMBOL } from '../../utils/constants';
import { DateConst, DateConverters } from '../../utils/date';
import { SReducerState as SPurchaseEntryReducer } from './reducers/purchaseEntry/types';
import { SCustomer, SPurchaseEntry, SPurchaseTableColumns } from './types';
import { PURCHASE_PAYMENT_TYPE } from '../../components/Common/PaymentFlow/PurchasePayment/constants';
import { formattedRupee, GlobalAppUtils, StoreUtils, truncate } from '../../utils/util';
import { ColumnType, RowMapType } from '../../components/Common/ProductSearchField/types';
import styles from '../BillEntry/features/BillingTable/table.module.scss';

export const INITIAL_STATE = {
  SPurchaseEntry: (): SPurchaseEntry => ({
    id: '',
    batchNo: '',
    packaging: '',
    discountPercent: '',
    discountPrice: '',
    expiry: null,
    freeQty: 0,
    lot: '',
    mrp: '',
    name: '',
    ptr: '',
    margin: 0,
    effPtr: '',
    qty: '',
    rack: '',
    rackId: '',
    skuId: '',
    hsnCode: '',
    barcode: '',
    gst: DEFAULT_GST,
    itemAmount: 0,
    netItemAmount: 0,
    gstAmount: 0,
    discountAmount: 0,
    overallItemDiscount: 0,
    batches: undefined,
    conversionFactor: false, // To track conversion ratio required Sku
    conversionRatio: 1,
  }),
  SCustomer: (): SCustomer => ({
    invoiceNo: '',
    invoicedOn: DateConst.todayUnix(),
    supplierId: '',
    supplierName: '',
    paymentMode: TYPE_OF_PAYMENT.Pending,
    note: '',
    creditAmount: 0,
    extraCharges: 0,
    expectedAmount: 0,
    bankId: '',
    paySplit: undefined,
    grnStatus: undefined,
    grnCompleted: undefined,
    wmsGrnDiscrepancy: undefined,

    tcs: 0,
    discount: 0,
    discountPercent: 0,

    poId: '',
  }),
  SPurchaseEntryReducer: (): SPurchaseEntryReducer => ({
    rows: [INITIAL_STATE.SPurchaseEntry()],
    total: {
      overallItemDiscount: 0,
      netTotalAmount: 0,
      totalDiscount: 0,
      totalGst: 0,
      totalValue: 0,
      roundOff: 0,
    },
    client: {
      mapItems: [],
      mappingModalOpen: false,
      page: 0,
      dueAmount: 0,
      totalCreditNotes: 0,
      vouchers: 0,
      pendingBills: [],
      templateMapModal: false,
      focusedRowIndex: 0,
      focusedColumnIndex: 0,
      searchProducts: {
        items: [],
        isFetching: false,
        isOpen: false,
        searchString: '',
      },
      submitData: {
        data: {} as SPurchaseEntryReducer['client']['submitData']['data'],
        showSplitView: false,
        showSubmitModal: false,
        allowSubmit: true,
        allowValidationSubmit: true,
      },
      conversionState: {
        isConversionRatioOpen: false,
        isConversionAlertOpen: false,
        selectedItems: [] as SPurchaseEntryReducer['client']['conversionState']['selectedItems'],
      },
      alertState: {
        isOpen: false,
        title: '',
        description: '',
        items: [],
        columns: [],
      },
      isSubmittedBillValid: true,
      inventoryViewSku: '',
      isUploadMode: false, // To track file upload mode
      isUploadCheckSku: false,
      lotModalOpen: false,
      racks: [],
      isPurchaseEdit: false,
      isAllowPurchaseEdit: true, // To restrict bill edit if there are multiple bills
      hideColumns: GET_HIDE_COLUMN_CONFIG(),
    },
    customer: {
      bankId: '',
      invoiceNo: '',
      invoicedOn: DateConst.todayUnix(),
      supplierId: '',
      supplierName: '',
      isFmcgSupplier: false,
      paymentMode: TYPE_OF_PAYMENT.Pending,
      note: '',
      creditAmount: 0,
      extraCharges: 0,
      expectedAmount: 0,
      tcs: 0,
      discount: 0,
      discountPercent: 0,
      uploadType: '',
    },
    discrepancy: {
      loading: false,
      itemWise: [],
      supplierWise: [],
    },
  }),
};

export const TYPE_OF_PAYMENT = { ...PURCHASE_PAYMENT_TYPE.WPending };

// KeyMaps
export const PurchaseEntryKeyMap = {
  name: 'name',
  conversionRatio: 'conversionRatio',
  qty: 'qty',
  batch: 'batchNo',
  expiry: 'expiry',
  mrp: 'mrp',
  ptr: 'ptr',
  discount: 'discountPercent',
  discountPrice: 'discountPrice',
  freeQty: 'freeQty',
  gst: 'gst',
  rack: 'rack',
  hsnCode: 'hsnCode',
  barcode: 'barcode',
  batches: 'batches',
};

const cRatioInfoMsg = `- Converts quantities between packaging \nlevels (e.g., box to strip).\n- Ensures accurate inventory calculations.\n- Standardizes pricing for invoice and sales.`;

export const PURCHASE_TABLE_COLUMN: SPurchaseTableColumns[] = [
  { name: '#', mandatory: true },
  {
    name: 'Product',
    info: PRODUCT_SEARCH_INFO_MESSAGE,
    mandatory: true,
  },
  { name: 'Pack' },
  { name: 'C.Ratio', info: cRatioInfoMsg, hideDefault: true },
  { name: 'Product Id', mandatory: true },
  { name: 'Barcode' },
  { name: 'Qty', mandatory: true },
  { name: 'Free Qty', mandatory: true },
  { name: 'Batch No', mandatory: true },
  { name: 'Expiry Date', mandatory: true },
  { name: 'MRP', mandatory: true },
  { name: 'PTR', mandatory: true },
  { name: 'Margin%' },
  { name: 'Rack' },
  { name: 'GST%' },
  { name: 'HSN' },
  { name: 'LOT' },
  { name: 'Disc%' },
  { name: `Disc${RS_SYMBOL}` },
  { name: 'Total', mandatory: true },
  { name: 'Actions', mandatory: true },
];

export const PRODUCT_SEARCH_TABLE_CONST: { columns: ColumnType[]; rowMapping: RowMapType[] } = {
  columns: [
    { key: 'Product' },
    { key: 'MRP' },
    { key: 'GST(%)' },
    { key: 'Stock' },
    { key: 'Rack' },
    { key: 'Pack' },
    { key: 'U.Ratio' },
    { key: 'Manufacturer' },
  ],
  rowMapping: [
    { key: 'name', width: '200', align: 'left' },
    {
      key: 'mrp',
      width: '90',
      align: 'left',
      formatter: (value) => formattedRupee(value as number),
      className: styles.productMrp,
    },
    { key: 'gst', width: '40', align: 'left' },
    { key: 'stock', align: 'left' },
    { key: 'rack', align: 'left' },
    {
      key: 'packaging',
      align: 'left',
      width: '110',
      formatter: (value) => GlobalAppUtils.formatPackaging(value as string),
    },
    { key: 'unitRatio', align: 'left' },
    {
      key: 'manufacturer',
      width: '150',
      align: 'left',
      formatter: (value) => truncate((value || '') as string),
    },
  ],
};

export const BRANCH_PO = 'branch-po';

export const allowAddOrDeleteItem = (orderType: string | undefined) => orderType !== BRANCH_PO;

export const ADD_NEW_SUPPLIER = 'Add New Supplier';

export const INITIAL_STATE_LOT_DATA = {
  lot: '',
  lotFree: 0,
  lotQty: 0,
  schemePercent: 0,
  schemeAmount: 0,
};

export const GET_HIDE_COLUMN_CONFIG = (): Record<string, boolean> => {
  const activeSession = StoreUtils.getActiveSession();
  const columnConfig = activeSession.session?.config?.webColumnConfig?.purchaseEntry;
  if (columnConfig && columnConfig?.['Quantity'] === true) {
    columnConfig['Qty'] = true;
  }
  if (columnConfig) return columnConfig;

  return PURCHASE_TABLE_COLUMN.reduce((acc, columnData: SPurchaseTableColumns) => {
    if (columnData.name) {
      acc[columnData.name] = columnData.hideDefault ? false : true;
    }
    return acc;
  }, {} as Record<string, boolean>);
};

export const MRTAlertColumnMap = {
  index: {
    accessorKey: 'index',
    header: '#',
    maxSize: 10,
    enableColumnFilter: false,
  },
  name: {
    accessorKey: 'name',
    header: 'Name',
  },
  packaging: {
    accessorKey: 'packaging',
    header: 'Packaging',
  },
  batch: {
    accessorKey: 'batchNo',
    header: 'Batch',
  },
  expiry: {
    accessorKey: 'expiry',
    header: 'Expiry',
    accessorFn: (row: SPurchaseEntry) => DateConverters.epochToExpiry(row.expiry as number),
  },
  qty: {
    accessorKey: 'qty',
    header: 'Qty',
  },
  freeQty: {
    accessorKey: 'freeQty',
    header: 'Free Qty',
  },
  mrp: {
    accessorKey: 'mrp',
    header: 'MRP',
    accessorFn: (row: SPurchaseEntry) => `${formattedRupee(row.mrp)}`,
  },
  ptr: {
    accessorKey: 'ptr',
    header: 'PTR',
    accessorFn: (row: SPurchaseEntry) => `${formattedRupee(row.ptr)}`,
  },
  netItemAmount: {
    accessorKey: 'netItemAmount',
    header: 'Total',
    accessorFn: (row: SPurchaseEntry) => `${formattedRupee(row.netItemAmount)}`,
  },
  reason: {
    accessorKey: 'reason',
    header: 'Reason',
  },
};
